(() => {
  const COLLECTION_SELECTOR = '[data-mr-main-collection]';

  const getFocusableElements = (container) => {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((element) => {
      return element.offsetParent !== null;
    });
  };

  const initializeCollection = (section) => {
    if (
      !section ||
      section.dataset.mrCollectionInitialized === 'true'
    ) {
      return;
    }

    const drawer = section.querySelector(
      '[data-mr-filter-drawer]'
    );

    const overlay = section.querySelector(
      '[data-mr-filter-overlay]'
    );

    const openButton = section.querySelector(
      '[data-mr-filter-open]'
    );

    const closeButton = section.querySelector(
      '[data-mr-filter-close]'
    );

    const form = section.querySelector(
      '[data-mr-filter-form]'
    );

    let lastFocusedElement = null;

    const drawerId =
      `collection-filter-${drawer?.id || 'main'}`;

    const performOpenDrawer = () => {
      if (!drawer || !overlay) return;

      lastFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : openButton;

      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');

      overlay.classList.add('is-visible');
      overlay.removeAttribute('tabindex');

      openButton?.setAttribute(
        'aria-expanded',
        'true'
      );

      window.requestAnimationFrame(() => {
        closeButton?.focus({
          preventScroll: true
        });
      });
    };

    const performCloseDrawer = ({
      restoreFocus = true
    } = {}) => {
      if (!drawer || !overlay) return;

      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');

      overlay.classList.remove('is-visible');
      overlay.setAttribute('tabindex', '-1');

      openButton?.setAttribute(
        'aria-expanded',
        'false'
      );

      if (
        restoreFocus &&
        lastFocusedElement instanceof HTMLElement
      ) {
        lastFocusedElement.focus({
          preventScroll: true
        });
      }
    };

    window.MRDrawerManager?.register(
      drawerId,
      {
        open: performOpenDrawer,
        close: performCloseDrawer,
        isOpen: () =>
          drawer?.classList.contains('is-open') === true
      }
    );

    openButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        if (window.MRDrawerManager) {
          window.MRDrawerManager.open(
            drawerId
          );
        } else {
          performOpenDrawer();
        }
      }
    );

    closeButton?.addEventListener(
      'click',
      () => {
        if (window.MRDrawerManager) {
          window.MRDrawerManager.close(
            drawerId
          );
        } else {
          performCloseDrawer();
        }
      }
    );

    overlay?.addEventListener(
      'click',
      () => {
        if (window.MRDrawerManager) {
          window.MRDrawerManager.close(
            drawerId
          );
        } else {
          performCloseDrawer();
        }
      }
    );

    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (window.MRDrawerManager) {
          window.MRDrawerManager.close(
            drawerId
          );
        } else {
          performCloseDrawer();
        }

        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(drawer);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      }

      if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    });

    const priceFilter = section.querySelector(
      '[data-mr-price-filter]'
    );

    if (priceFilter) {
      const rangeWrap = priceFilter.querySelector(
        '[data-mr-price-range]'
      );

      const minRange = priceFilter.querySelector(
        '[data-mr-price-min]'
      );

      const maxRange = priceFilter.querySelector(
        '[data-mr-price-max]'
      );

      const minNumber = priceFilter.querySelector(
        '[data-mr-price-min-number]'
      );

      const maxNumber = priceFilter.querySelector(
        '[data-mr-price-max-number]'
      );

      const normalizeValues = (
        changedControl = null
      ) => {
        if (
          !rangeWrap ||
          !minRange ||
          !maxRange ||
          !minNumber ||
          !maxNumber
        ) {
          return;
        }

        const maximum = Number(maxRange.max) || 0;

        let minimumValue =
          Number(minRange.value) || 0;

        let maximumValue =
          Number(maxRange.value) || maximum;

        if (minimumValue > maximumValue) {
          if (
            changedControl === minRange ||
            changedControl === minNumber
          ) {
            maximumValue = minimumValue;
          } else {
            minimumValue = maximumValue;
          }
        }

        minimumValue = Math.max(
          0,
          Math.min(minimumValue, maximum)
        );

        maximumValue = Math.max(
          0,
          Math.min(maximumValue, maximum)
        );

        minRange.value = String(minimumValue);
        maxRange.value = String(maximumValue);

        minNumber.value = String(minimumValue);
        maxNumber.value = String(maximumValue);

        const minPercent = maximum > 0
          ? (minimumValue / maximum) * 100
          : 0;

        const maxPercent = maximum > 0
          ? (maximumValue / maximum) * 100
          : 100;

        rangeWrap.style.setProperty(
          '--mr-price-min-percent',
          `${minPercent}%`
        );

        rangeWrap.style.setProperty(
          '--mr-price-max-percent',
          `${maxPercent}%`
        );
      };

      [minRange, maxRange].forEach((input) => {
        input?.addEventListener('input', () => {
          normalizeValues(input);
        });
      });

      [minNumber, maxNumber].forEach((input) => {
        input?.addEventListener('input', () => {
          normalizeValues(input);
        });
      });

      normalizeValues();
    }

    const updateSelectionCount = () => {
      if (!form) return;

      const counter = form.querySelector(
        '[data-mr-filter-selection-count]'
      );

      if (!counter) return;

      const checkedFilters = Array.from(
        form.querySelectorAll(
          'input[type="checkbox"]:checked'
        )
      ).length;

      const priceMin = form.querySelector(
        '[data-mr-price-min-number]'
      );

      const priceMax = form.querySelector(
        '[data-mr-price-max-number]'
      );

      let count = checkedFilters;

      if (priceMin && priceMax) {
        const min = Number(priceMin.value) || 0;
        const max = Number(priceMax.value) || 0;
        const absoluteMax =
          Number(priceMax.max) || max;

        if (min > 0 || max < absoluteMax) {
          count += 1;
        }
      }

      counter.textContent = `(${count})`;
    };

    form?.addEventListener('change', updateSelectionCount);
    form?.addEventListener('input', updateSelectionCount);

    form?.addEventListener('submit', () => {
      form.querySelectorAll(
        'input[type="number"]'
      ).forEach((input) => {
        if (
          input.value === '' ||
          Number(input.value) === 0
        ) {
          if (input.hasAttribute('data-mr-price-min-number')) {
            input.disabled = true;
          }
        }

        if (input.hasAttribute('data-mr-price-max-number')) {
          const max = Number(input.max);
          const value = Number(input.value);

          if (value >= max) {
            input.disabled = true;
          }
        }
      });
    });

    section.dataset.mrCollectionInitialized = 'true';
  };

  const updateCardFromSwatch = (button) => {
    const card = button.closest('[data-mr-product-card]');

    if (!card) return;

    const image = card.querySelector(
      '.mr-collection-card__image'
    );

    const price = card.querySelector(
      '[data-mr-card-price]'
    );

    const addButton = card.querySelector(
      '[data-mr-card-add]'
    );

    card
      .querySelectorAll('[data-mr-card-swatch]')
      .forEach((swatch) => {
        swatch.classList.toggle(
          'is-active',
          swatch === button
        );
      });

    if (image && button.dataset.imageSrc) {
      image.src = button.dataset.imageSrc;
      image.removeAttribute('srcset');

      if (button.dataset.imageAlt) {
        image.alt = button.dataset.imageAlt;
      }
    }

    if (price && button.dataset.price) {
      price.textContent = button.dataset.price;
    }

    if (addButton && button.dataset.variantId) {
      addButton.dataset.variantId =
        button.dataset.variantId;

      const isAvailable =
        button.dataset.available === 'true';

      addButton.disabled = !isAvailable;

      addButton.textContent = isAvailable
        ? 'ADD TO CART'
        : 'SOLD OUT';
    }
  };

  const addVariant = async (button) => {
    const variantId = Number.parseInt(
      button.dataset.variantId || '',
      10
    );

    if (!variantId || button.disabled) return;

    const originalText = button.textContent;

    button.disabled = true;
    button.textContent = 'ADDING…';

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ]
          })
        }
      );

      if (!response.ok) {
        throw new Error('Unable to add product.');
      }

      button.textContent = 'ADDED';

      document.dispatchEvent(
        new CustomEvent('mr:cart-updated')
      );

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1400);
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.textContent = 'TRY AGAIN';
    }
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest(
      '[data-mr-card-swatch]'
    );

    if (swatch) {
      updateCardFromSwatch(swatch);
      return;
    }

    const addButton = event.target.closest(
      '[data-mr-card-add]'
    );

    if (addButton) {
      addVariant(addButton);
    }
  });

  const initializeAll = (root = document) => {
    root
      .querySelectorAll(COLLECTION_SELECTOR)
      .forEach(initializeCollection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll()
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(event.target);
    }
  );
})();

/* MR PRICE INPUT HARD FIX START */

(() => {
  const NUMBER_SELECTOR =
    'input[type="number"][name*="filter.v.price"]';

  const isPriceNumberInput = (element) => {
    return element instanceof HTMLInputElement &&
      element.matches(NUMBER_SELECTOR);
  };

  const getPriceInputs = (input) => {
    const scope =
      input.closest(
        '[data-mr-price-filter], ' +
        '.mr-filter-price, ' +
        '.mr-filter-drawer__price, ' +
        '.mr-collection-filter__price, ' +
        'details'
      ) || input.form || document;

    const inputs = Array.from(
      scope.querySelectorAll(NUMBER_SELECTOR)
    );

    const minimumInput = inputs.find((field) => {
      return field.name.includes('price.gte');
    }) || inputs[0];

    const maximumInput = inputs.find((field) => {
      return field.name.includes('price.lte');
    }) || inputs[1];

    return {
      scope,
      minimumInput,
      maximumInput
    };
  };

  const parseNumber = (value) => {
    if (
      value === '' ||
      value === null ||
      value === undefined
    ) {
      return null;
    }

    const normalized = String(value)
      .replace(',', '.')
      .replace(/[^\d.-]/g, '');

    const number = Number(normalized);

    return Number.isFinite(number)
      ? number
      : null;
  };

  const clamp = (value, minimum, maximum) => {
    return Math.min(
      Math.max(value, minimum),
      maximum
    );
  };

  const formatInputValue = (value) => {
    if (!Number.isFinite(value)) {
      return '';
    }

    return Number.isInteger(value)
      ? String(value)
      : String(
          Math.round(value * 100) / 100
        );
  };

  const syncRangeInputs = (
    scope,
    minimumValue,
    maximumValue
  ) => {
    const ranges = Array.from(
      scope.querySelectorAll(
        'input[type="range"]'
      )
    );

    if (!ranges.length) return;

    const minimumRange =
      ranges.find((range) => {
        return (
          range.matches(
            '[data-price-range-min], ' +
            '[data-mr-price-range-min]'
          ) ||
          range.name?.includes('price.gte')
        );
      }) || ranges[0];

    const maximumRange =
      ranges.find((range) => {
        return (
          range.matches(
            '[data-price-range-max], ' +
            '[data-mr-price-range-max]'
          ) ||
          range.name?.includes('price.lte')
        );
      }) || ranges[1];

    if (
      minimumRange &&
      Number.isFinite(minimumValue)
    ) {
      minimumRange.value =
        String(minimumValue);

      minimumRange.dispatchEvent(
        new Event('input', {
          bubbles: true
        })
      );
    }

    if (
      maximumRange &&
      Number.isFinite(maximumValue)
    ) {
      maximumRange.value =
        String(maximumValue);

      maximumRange.dispatchEvent(
        new Event('input', {
          bubbles: true
        })
      );
    }
  };

  const commitPriceInput = (input) => {
    const {
      scope,
      minimumInput,
      maximumInput
    } = getPriceInputs(input);

    if (!minimumInput && !maximumInput) {
      return;
    }

    const fieldMaximum = Math.max(
      parseNumber(minimumInput?.max) || 0,
      parseNumber(maximumInput?.max) || 0,
      parseNumber(
        scope.querySelector(
          'input[type="range"]'
        )?.max
      ) || 0,
      999999
    );

    let minimumValue =
      parseNumber(minimumInput?.value);

    let maximumValue =
      parseNumber(maximumInput?.value);

    if (minimumValue === null) {
      minimumValue = 0;
    }

    if (maximumValue === null) {
      maximumValue = fieldMaximum;
    }

    minimumValue = clamp(
      minimumValue,
      0,
      fieldMaximum
    );

    maximumValue = clamp(
      maximumValue,
      0,
      fieldMaximum
    );

    if (minimumValue > maximumValue) {
      if (input === minimumInput) {
        maximumValue = minimumValue;
      } else {
        minimumValue = maximumValue;
      }
    }

    if (minimumInput) {
      minimumInput.value =
        minimumValue === 0
          ? ''
          : formatInputValue(minimumValue);
    }

    if (maximumInput) {
      maximumInput.value =
        maximumValue >= fieldMaximum
          ? ''
          : formatInputValue(maximumValue);
    }

    syncRangeInputs(
      scope,
      minimumValue,
      maximumValue
    );
  };

  /*
   * Alte delegierte Input-Handler dürfen die Zahl während
   * des Tippens nicht sofort wieder durch den Sliderwert
   * ersetzen.
   */
  document.addEventListener(
    'input',
    (event) => {
      if (!isPriceNumberInput(event.target)) {
        return;
      }

      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (!isPriceNumberInput(event.target)) {
        return;
      }

      /*
       * Standardverhalten bleibt aktiv:
       * - Zahlen eingeben
       * - Backspace/Delete
       * - Pfeile hoch/runter
       * - Tab
       */
      event.stopImmediatePropagation();

      if (event.key === 'Enter') {
        event.preventDefault();
        commitPriceInput(event.target);
        event.target.blur();
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      if (!isPriceNumberInput(event.target)) {
        return;
      }

      event.stopImmediatePropagation();
      commitPriceInput(event.target);
    },
    true
  );

  document.addEventListener(
    'blur',
    (event) => {
      if (!isPriceNumberInput(event.target)) {
        return;
      }

      commitPriceInput(event.target);
    },
    true
  );
})();

/* MR PRICE INPUT HARD FIX END */

/* MR COLLECTION SWATCH COLORS START */
(() => {
  const SWATCH_SELECTOR = '.mr-collection-card__swatch';

  const COLOR_MAP = {
    navy: '#081e3d',
    'navy blue': '#081e3d',
    'dark navy': '#081e3d',
    'dark blue': '#081e3d',
    midnight: '#081e3d',
    'midnight blue': '#081e3d',

    blue: '#165dba',
    royal: '#2147a8',
    'royal blue': '#2147a8',
    lightblue: '#add8e6',
    'light blue': '#add8e6',
    sky: '#87ceeb',
    'sky blue': '#87ceeb',

    turquoise: '#03fae1',
    teal: '#008080',
    aqua: '#00ffff',

    black: '#000000',
    charcoal: '#36454f',
    anthracite: '#383e42',

    white: '#ffffff',
    'off white': '#f7f5ef',
    offwhite: '#f7f5ef',
    natural: '#eee9dc',
    cream: '#fffdd0',
    ivory: '#fffff0',

    grey: '#9b9b9b',
    gray: '#9b9b9b',
    'light grey': '#d3d3d3',
    'light gray': '#d3d3d3',
    'dark grey': '#555555',
    'dark gray': '#555555',
    heather: '#a7a7a7',
    'sport grey': '#a7a7a7',

    beige: '#d9c7a3',
    sand: '#cbb994',
    tan: '#d2b48c',
    khaki: '#b5a56a',

    brown: '#795548',
    chocolate: '#5d4037',

    red: '#d71920',
    burgundy: '#800020',
    maroon: '#800000',

    pink: '#f39ac1',
    'light pink': '#f7bfd8',
    hotpink: '#ff69b4',
    'hot pink': '#ff69b4',

    purple: '#7b3fa1',
    lavender: '#b57edc',

    green: '#2e7d32',
    olive: '#808000',
    mint: '#98ff98',
    forest: '#1b5e20',
    'forest green': '#1b5e20',

    yellow: '#ffd600',
    gold: '#d4af37',
    mustard: '#d4a017',

    orange: '#ff7a00',
    coral: '#ff7f50'
  };

  const normalizeColorName = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getSwatchName = (swatch) => {
    const candidates = [
      swatch.dataset.color,
      swatch.dataset.optionValue,
      swatch.dataset.value,
      swatch.getAttribute('aria-label'),
      swatch.getAttribute('title'),
      swatch.textContent
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      let value = String(candidate)
        .replace(/^color\s*:\s*/i, '')
        .replace(/^colour\s*:\s*/i, '')
        .replace(/^select\s+/i, '')
        .trim();

      if (value) return normalizeColorName(value);
    }

    return '';
  };

  const isValidCssColor = (value) => {
    if (!value) return false;

    const test = document.createElement('span');
    test.style.color = '';
    test.style.color = value;

    return test.style.color !== '';
  };

  const resolveColor = (name) => {
    if (!name) return null;

    if (COLOR_MAP[name]) {
      return COLOR_MAP[name];
    }

    const partialMatch = Object.keys(COLOR_MAP).find((key) => {
      return name.includes(key);
    });

    if (partialMatch) {
      return COLOR_MAP[partialMatch];
    }

    const cssCandidate = name.replace(/\s+/g, '');

    if (isValidCssColor(cssCandidate)) {
      return cssCandidate;
    }

    return null;
  };

  const applySwatchColors = (scope = document) => {
    scope.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
      const name = getSwatchName(swatch);
      const color = resolveColor(name);

      if (!color) return;

      swatch.style.setProperty(
        '--mr-variant-swatch-color',
        color
      );

      swatch.style.setProperty(
        'background-color',
        color,
        'important'
      );

      swatch.dataset.mrResolvedColor = color;

      const isLight =
        [
          'white',
          'off white',
          'offwhite',
          'natural',
          'cream',
          'ivory',
          'light grey',
          'light gray'
        ].some((lightName) => name.includes(lightName));

      swatch.classList.toggle(
        'is-light-swatch',
        isLight
      );
    });
  };

  const initializeSwatches = () => {
    applySwatchColors(document);
  };

  document.addEventListener(
    'DOMContentLoaded',
    initializeSwatches
  );

  document.addEventListener(
    'shopify:section:load',
    initializeSwatches
  );

  const observer = new MutationObserver((mutations) => {
    let shouldUpdate = false;

    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length > 0) {
        shouldUpdate = true;
      }
    });

    if (shouldUpdate) {
      applySwatchColors(document);
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  initializeSwatches();
})();
/* MR COLLECTION SWATCH COLORS END */
